import { MissingVarError } from '@execonline-inc/environment';
import { warn } from '@execonline-inc/logging';
import { toResult } from '@execonline-inc/maybe-adapter';
import { assertNever, noop } from '@kofno/piper';
import { ok } from 'resulty';
import { Task } from 'taskarian';
import { Authenticated } from '../../CurrentUserStore/Types';
import { sessionStore } from '../../Session/Store';
import { fromResult } from '../../TaskExt';
import { dataLayer } from './Loader';

export interface MissingValue {
  kind: 'missing-value';
  value: string;
}

export const missingValue = (value: string): MissingValue => ({
  kind: 'missing-value',
  value,
});

export interface UserLayer {
  event: string;
  id: string;
  salesforceAccountId: string;
  platformOrgId: string;
  platformOrgName: string;
  platformUserId: string;
  salesforceContactId: string;
  impersonatingUserId: string;
  salesforcePlatformOrgId: string;
  productLicenseIds: number[];
  productLicenseTypeIds: number[];
  epPrivilegeIds: number[];
}

interface Properties {
  platformUserId: string;
  salesforceContactId: string;
}

interface KnownProperties {
  event: string;
  id: string;
  salesforceAccountId: string;
  platformOrgId: string;
  platformOrgName: string;
  impersonatingUserId: string;
  salesforcePlatformOrgId: string;
  productLicenseIds: number[];
  productLicenseTypeIds: number[];
  epPrivilegeIds: number[];
}

export interface CustomEventLayer {
  event: string;
  action: string;
}

export type DataLayer = UserLayer | CustomEventLayer;

export type PageTitle = { pageTitle: string; event: string };

export interface GoogleTagManager {
  dataLayerFn: DataLayerFn;
}

interface DataLayerFn {
  set: (key: string, value: string) => void;
}

export interface GtmDataLayer {
  dataLayer: DataLayer[];
}

export type GtmInitializationError = MissingVarError | GtmLoadError;

export interface GtmLoadError {
  kind: 'gtm-load-error';
  key: string;
}

export const gtmLoadError = (key: string): GtmLoadError => ({
  kind: 'gtm-load-error',
  key,
});

export interface GtmOperationError {
  kind: 'gtm-operation-error';
}

export const gtmOperationError = (): GtmOperationError => ({
  kind: 'gtm-operation-error',
});

const impersonatingUserId = (): string => {
  switch (sessionStore.state.kind) {
    case 'requesting':
    case 'absent':
    case 'errored':
      return '';
    case 'present':
      return sessionStore.state.sessionData.impersonatingUserId.map(String).getOrElseValue('');
  }
};

type AnalyticsError = MissingValue | GtmOperationError;

export const identifyUser = (user: Authenticated): Task<AnalyticsError, void> => {
  const {
    id,
    platformUserId,
    contactUID,
    organizationId,
    organizationName,
    organizationAccountUID,
    organizationPlatformOrgUID,
    productLicenseIds,
    productLicenseTypeIds,
    epPrivilegeIds,
  } = user.currentUserResource.payload;

  const knownProperties = {
    event: 'user_data',
    id: String(id),
    salesforceAccountId: organizationAccountUID,
    platformOrgId: String(organizationId),
    platformOrgName: organizationName,
    impersonatingUserId: impersonatingUserId(),
    salesforcePlatformOrgId: organizationPlatformOrgUID,
    productLicenseIds: productLicenseIds,
    productLicenseTypeIds: productLicenseTypeIds,
    epPrivilegeIds: epPrivilegeIds,
  };

  const valuesResult = ok<AnalyticsError, {}>({})
    .assign('platformUserId', toResult(missingValue('platformUserId'), platformUserId.map(String)))
    .assign('salesforceContactId', toResult(missingValue('contactUID'), contactUID));

  return fromResult(valuesResult).andThen((valuesResult) =>
    setUserProperties(valuesResult, knownProperties),
  );
};

const setUserProperties = (
  properties: Properties,
  knownProperties: KnownProperties,
): Task<AnalyticsError, void> => {
  return new Task<AnalyticsError, void>((reject, resolve) => {
    try {
      dataLayer({ ...properties, ...knownProperties });
      resolve();
    } catch (e) {
      reject(gtmOperationError());
    }
    return noop;
  });
};

export const handleAnalyticsError = (e: AnalyticsError) => {
  switch (e.kind) {
    case 'missing-value':
      warn(`Analytics cannot properly identify current user without value for ${e.value}`);
      break;
    case 'gtm-operation-error':
      warn('An error occurred during an analytics operation.');
      break;
    default:
      assertNever(e);
  }
};
